import React, { useEffect, useMemo, useState } from 'react';
import { speakersService } from '../../services/speakersService';

function LaboratoriesManagement({ selectedEdition }) {
  const [labs, setLabs] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    objectives: '',
    themes: '',
    value: '',
    partnerLogos: '',
    partnerDescription: '',
    pdfPath: '',
    pdfUrl: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [assignment, setAssignment] = useState({
    speakerIds: [],
    role: 'international_expert',
    displayOrder: 0
  });
  const [speakerSelectionData, setSpeakerSelectionData] = useState({});
  const [speakerSearchTerm, setSpeakerSearchTerm] = useState('');
  const [showCreateSpeakerForm, setShowCreateSpeakerForm] = useState(false);
  const [creatingSpeaker, setCreatingSpeaker] = useState(false);
  const [newSpeakerForm, setNewSpeakerForm] = useState({
    name: '',
    title: '',
    organization: '',
    country: '',
    mainCategory: 'international',
    type: 'regular',
    displayOrder: 0
  });

  useEffect(() => {
    if (selectedEdition) {
      loadData();
    }
  }, [selectedEdition]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [labsData, speakersData] = await Promise.all([
        speakersService.getLaboratories(selectedEdition),
        speakersService.getSpeakersForLabManagement(selectedEdition)
      ]);
      setLabs(labsData || []);
      setSpeakers(speakersData || []);
      if (!selectedLabId && (labsData || []).length > 0) {
        setSelectedLabId(String(labsData[0].id));
      }
    } catch (error) {
      console.error('Error loading laboratories data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedLab = useMemo(() => labs.find(lab => String(lab.id) === String(selectedLabId)) || null, [labs, selectedLabId]);

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      objectives: '',
      themes: '',
      value: '',
      partnerLogos: '',
      partnerDescription: '',
      pdfPath: '',
      pdfUrl: ''
    });
  };

  const handleEditLab = (lab) => {
    setEditingId(lab.id);
    setSelectedLabId(String(lab.id));
    setFormData({
      title: lab.title || '',
      subtitle: lab.subtitle || '',
      description: lab.description || '',
      objectives: lab.objectives || '',
      themes: lab.themes || '',
      value: lab.value || '',
      partnerLogos: lab.partner_logos || lab.partnerLogos || '',
      partnerDescription: lab.partner_description || lab.partnerDescription || '',
      pdfPath: lab.pdf_path || lab.pdfPath || '',
      pdfUrl: lab.pdf_url || lab.pdfUrl || ''
    });
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploadingPdf(true);
      const uploadResult = await speakersService.uploadLaboratoryPDF(file, selectedEdition);
      setFormData(prev => ({
        ...prev,
        pdfUrl: uploadResult.url,
        pdfPath: uploadResult.path
      }));
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Erreur lors du téléchargement du PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSaveLab = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      alert('Le titre du laboratoire est obligatoire.');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await speakersService.updateLaboratory(editingId, formData);
      } else {
        await speakersService.createLaboratory(formData, selectedEdition);
      }
      await loadData();
      resetForm();
    } catch (error) {
      console.error('Error saving laboratory:', error);
      alert('Erreur lors de la sauvegarde du laboratoire.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLab = async (labId) => {
    if (!window.confirm('Supprimer ce laboratoire et ses associations ?')) return;
    try {
      await speakersService.deleteLaboratory(labId);
      await loadData();
      if (selectedLabId === String(labId)) {
        setSelectedLabId('');
      }
    } catch (error) {
      console.error('Error deleting laboratory:', error);
      alert('Erreur lors de la suppression du laboratoire.');
    }
  };

  const handleAssignSpeaker = async (e) => {
    e.preventDefault();
    if (!selectedLabId || !assignment.speakerIds.length) {
      alert('Veuillez sélectionner au moins un intervenant.');
      return;
    }

    try {
      setSaving(true);
      await Promise.all(
        assignment.speakerIds.map((speakerId) => {
          const selection = speakerSelectionData[String(speakerId)] || {};
          return speakersService.assignSpeakerToLaboratory({
            speakerId,
            laboratoryId: selectedLabId,
            role: selection.role || 'international_expert',
            displayOrder: Number(selection.displayOrder || 0)
          });
        })
      );
      await loadData();
      setAssignment({ speakerIds: [], role: 'international_expert', displayOrder: 0 });
      setSpeakerSelectionData({});
    } catch (error) {
      console.error('Error assigning speakers:', error);
      alert('Erreur lors de l’association des intervenants.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSpeaker = async (e) => {
    e.preventDefault();
    if (!newSpeakerForm.name?.trim()) {
      alert('Le nom de l’intervenant est obligatoire.');
      return;
    }

    try {
      setCreatingSpeaker(true);
      const createdSpeaker = await speakersService.addSpeaker(
        {
          ...newSpeakerForm,
          displayOrder: Number(newSpeakerForm.displayOrder || 0)
        },
        selectedEdition,
        newSpeakerForm.type || 'regular'
      );

      await loadData();
      setNewSpeakerForm({
        name: '',
        title: '',
        organization: '',
        country: '',
        mainCategory: 'international',
        type: 'regular',
        displayOrder: 0
      });
      setShowCreateSpeakerForm(false);
      setAssignment((prev) => ({
        ...prev,
        speakerIds: [...prev.speakerIds.filter((id) => String(id) !== String(createdSpeaker.id)), String(createdSpeaker.id)]
      }));
      setSpeakerSelectionData((prev) => ({
        ...prev,
        [String(createdSpeaker.id)]: { role: 'international_expert', displayOrder: 0 }
      }));
    } catch (error) {
      console.error('Error creating speaker:', error);
      alert('Erreur lors de la création de l’intervenant.');
    } finally {
      setCreatingSpeaker(false);
    }
  };

  const toggleSpeakerSelection = (speakerId) => {
    const speakerKey = String(speakerId);
    const isSelected = assignment.speakerIds.includes(speakerKey);

    setAssignment((prev) => ({
      ...prev,
      speakerIds: isSelected
        ? prev.speakerIds.filter((id) => String(id) !== speakerKey)
        : [...prev.speakerIds, speakerKey]
    }));

    setSpeakerSelectionData((prev) => {
      const next = { ...prev };
      if (isSelected) {
        delete next[speakerKey];
      } else {
        next[speakerKey] = { role: 'international_expert', displayOrder: 0 };
      }
      return next;
    });
  };

  const updateSelectedSpeakerAssignment = (speakerId, field, value) => {
    const speakerKey = String(speakerId);
    setSpeakerSelectionData((prev) => ({
      ...prev,
      [speakerKey]: {
        ...(prev[speakerKey] || {}),
        [field]: field === 'displayOrder' ? Number(value) : value
      }
    }));
  };

  const handleRemoveSpeaker = async (speakerId) => {
    if (!selectedLabId) return;
    try {
      await speakersService.removeSpeakerFromLaboratory(speakerId, selectedLabId);
      await loadData();
    } catch (error) {
      console.error('Error removing speaker assignment:', error);
      alert('Erreur lors de la suppression de l’association.');
    }
  };

  const rolePriority = {
    president: 0,
    international_expert: 1,
    academic_expert: 2,
    security_expert: 3
  };

  const filteredSpeakers = useMemo(() => {
    const term = speakerSearchTerm.trim().toLowerCase();
    if (!term) return speakers || [];

    return (speakers || []).filter((speaker) => {
      const haystack = `${speaker.name || ''} ${speaker.title || ''} ${speaker.organization || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [speakerSearchTerm, speakers]);

  const assignedSpeakers = useMemo(() => {
    if (!selectedLab) return [];

    return (speakers || [])
      .filter(speaker => (speaker.laboratoryAssignments || []).some(member => String(member.laboratory_id) === String(selectedLab.id)))
      .map(speaker => {
        const currentAssignment = (speaker.laboratoryAssignments || []).find(member => String(member.laboratory_id) === String(selectedLab.id));
        return {
          ...speaker,
          currentAssignment
        };
      })
      .sort((a, b) => {
        const roleA = rolePriority[a.currentAssignment?.role] ?? 99;
        const roleB = rolePriority[b.currentAssignment?.role] ?? 99;
        if (roleA !== roleB) return roleA - roleB;
        const orderA = a.currentAssignment?.display_order || 0;
        const orderB = b.currentAssignment?.display_order || 0;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '', 'fr');
      });
  }, [selectedLab, speakers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Gestion des laboratoires</h3>
        <p style={{ marginTop: 0, color: '#666' }}>Créez un laboratoire, renseignez ses informations et associez-y les intervenants.</p>

        <form onSubmit={handleSaveLab} style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Titre du laboratoire</label>
              <input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Sous-titre</label>
              <input value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ ...inputStyle, minHeight: '90px' }} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Objectifs</label>
            <textarea value={formData.objectives} onChange={(e) => setFormData({ ...formData, objectives: e.target.value })} style={{ ...inputStyle, minHeight: '90px' }} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Principales thématiques</label>
            <textarea value={formData.themes} onChange={(e) => setFormData({ ...formData, themes: e.target.value })} style={{ ...inputStyle, minHeight: '90px' }} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Valeur ajoutée</label>
            <textarea value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} style={{ ...inputStyle, minHeight: '90px' }} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Logos partenaires / établissements</label>
            <textarea
              value={formData.partnerLogos}
              onChange={(e) => setFormData({ ...formData, partnerLogos: e.target.value })}
              placeholder="URL1, URL2, URL3"
              style={{ ...inputStyle, minHeight: '90px' }}
            />
            <small style={{ color: '#6b7280' }}>Séparez les URLs par des virgules ou utilisez un JSON valide.</small>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Description des partenaires</label>
            <textarea value={formData.partnerDescription} onChange={(e) => setFormData({ ...formData, partnerDescription: e.target.value })} style={{ ...inputStyle, minHeight: '90px' }} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Document PDF du laboratoire</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfUpload}
                disabled={uploadingPdf}
                style={{ ...inputStyle, padding: '8px' }}
              />
              {uploadingPdf && <span style={{ color: '#002b5b', fontWeight: 600 }}>Téléchargement...</span>}
            </div>
            {formData.pdfUrl && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <a
                  href={formData.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#10b981', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
                >
                  <i className="pi pi-file-pdf" style={{ fontSize: '1.2rem' }}></i>
                  Voir le PDF actuel
                </a>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, pdfUrl: '', pdfPath: '' }))}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                >
                  Supprimer le PDF
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>{editingId ? 'Mettre à jour' : 'Créer le laboratoire'}</button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>Annuler</button>
          </div>
        </form>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '24px' }}>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
          <h4 style={{ marginTop: 0 }}>Laboratoires existants</h4>
          {loading ? (
            <p>Chargement...</p>
          ) : labs.length === 0 ? (
            <p>Aucun laboratoire pour cette édition.</p>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {labs.map((lab) => (
                <div key={lab.id} style={{ border: String(selectedLabId) === String(lab.id) ? '2px solid #002b5b' : '1px solid #e5e7eb', borderRadius: '10px', padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <strong>{lab.title}</strong>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" className="btn btn-secondary" onClick={() => handleEditLab(lab)} style={{ padding: '6px 10px' }}>Éditer</button>
                      <button type="button" className="btn btn-danger" onClick={() => handleDeleteLab(lab.id)} style={{ padding: '6px 10px' }}>Supprimer</button>
                    </div>
                  </div>
                  {lab.subtitle && <div style={{ color: '#666', marginTop: '4px' }}>{lab.subtitle}</div>}
                  <button type="button" className="btn btn-primary" onClick={() => setSelectedLabId(String(lab.id))} style={{ marginTop: '10px', padding: '6px 10px' }}>
                    Sélectionner ce laboratoire
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
          <h4 style={{ marginTop: 0 }}>Associer des intervenants</h4>
          {!selectedLab ? (
            <p>Sélectionnez d’abord un laboratoire dans la liste.</p>
          ) : (
            <>
              <div style={{ marginBottom: '10px', padding: '10px', background: '#f8fafc', borderRadius: '8px' }}>
                <strong>{selectedLab.title}</strong>
                {selectedLab.subtitle && <div style={{ color: '#666' }}>{selectedLab.subtitle}</div>}
              </div>

              <form onSubmit={handleAssignSpeaker} style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 600 }}>Intervenants</label>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateSpeakerForm((prev) => !prev)} style={{ padding: '6px 10px' }}>
                      {showCreateSpeakerForm ? 'Masquer' : 'Créer un intervenant'}
                    </button>
                  </div>

                  {showCreateSpeakerForm && (
                    <form onSubmit={handleCreateSpeaker} style={{ display: 'grid', gap: '10px', marginBottom: '10px', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f8fafc' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Nom</label>
                          <input value={newSpeakerForm.name} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, name: e.target.value })} style={inputStyle} required />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Titre</label>
                          <input value={newSpeakerForm.title} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, title: e.target.value })} style={inputStyle} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Organisation</label>
                          <input value={newSpeakerForm.organization} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, organization: e.target.value })} style={inputStyle} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Pays</label>
                          <input value={newSpeakerForm.country} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, country: e.target.value })} style={inputStyle} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Catégorie</label>
                          <select value={newSpeakerForm.mainCategory} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, mainCategory: e.target.value })} style={inputStyle}>
                            <option value="international">International</option>
                            <option value="marocain">Marocain</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Type</label>
                          <select value={newSpeakerForm.type} onChange={(e) => setNewSpeakerForm({ ...newSpeakerForm, type: e.target.value })} style={inputStyle}>
                            <option value="regular">Confirmé</option>
                            <option value="online">En ligne</option>
                          </select>
                        </div>
                      </div>
                      <button type="submit" className="btn btn-primary" disabled={creatingSpeaker}>Créer et sélectionner</button>
                    </form>
                  )}

                  <input
                    type="text"
                    placeholder="Rechercher un intervenant..."
                    value={speakerSearchTerm}
                    onChange={(e) => setSpeakerSearchTerm(e.target.value)}
                    style={{ ...inputStyle, marginBottom: '8px' }}
                  />

                  <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px', background: '#fff' }}>
                    {filteredSpeakers.length === 0 ? (
                      <p style={{ margin: 0, color: '#666' }}>Aucun intervenant disponible.</p>
                    ) : (
                      filteredSpeakers.map((speaker) => {
                        const checked = assignment.speakerIds.includes(String(speaker.id));
                        return (
                          <label key={speaker.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSpeakerSelection(speaker.id)} />
                            <span>{speaker.name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {assignment.speakerIds.length > 0 && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <label style={{ fontWeight: 600 }}>Rôle et ordre d’affichage par intervenant</label>
                    {assignment.speakerIds.map((speakerId) => {
                      const speaker = speakers.find((item) => String(item.id) === String(speakerId));
                      if (!speaker) return null;
                      return (
                        <div key={speakerId} style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 0.8fr', gap: '8px', alignItems: 'center', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f8fafc' }}>
                          <div style={{ fontWeight: 600 }}>{speaker.name}</div>
                          <select
                            value={speakerSelectionData[String(speakerId)]?.role || 'international_expert'}
                            onChange={(e) => updateSelectedSpeakerAssignment(speakerId, 'role', e.target.value)}
                            style={inputStyle}
                          >
                            <option value="president">Président du laboratoire</option>
                            <option value="international_expert">Expert international</option>
                            <option value="academic_expert">Expert académique</option>
                            <option value="security_expert">Experts sécurité</option>
                          </select>
                          <input
                            type="number"
                            value={speakerSelectionData[String(speakerId)]?.displayOrder ?? 0}
                            onChange={(e) => updateSelectedSpeakerAssignment(speakerId, 'displayOrder', e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '8px', color: '#4b5563', fontSize: '0.95rem' }}>
                  Les présidents sont affichés en premier, puis les experts internationaux, puis les experts académiques.
                </div>

                <button type="submit" className="btn btn-primary" disabled={saving}>Associer la sélection</button>
              </form>

              <div style={{ marginTop: '16px' }}>
                <h5 style={{ marginBottom: '8px' }}>Intervenants associés</h5>
                {assignedSpeakers.length === 0 ? (
                  <p>Aucun intervenant associé pour le moment.</p>
                ) : (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {assignedSpeakers.map((speaker) => (
                      <div key={speaker.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px' }}>
                        <div>
                          <strong>{speaker.name}</strong>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>
                            {speaker.currentAssignment?.roleLabel || speaker.roleLabel || speaker.role || 'Non défini'}
                          </div>
                          <div style={{ color: '#002b5b', fontSize: '0.8rem', marginTop: '2px' }}>
                            Ordre: {speaker.currentAssignment?.display_order ?? 0}
                          </div>
                        </div>
                        <button type="button" className="btn btn-danger" onClick={() => handleRemoveSpeaker(speaker.id)} style={{ padding: '6px 10px' }}>Retirer</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  boxSizing: 'border-box'
};

export default LaboratoriesManagement;
